// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

import { useCallback } from "react"
import { useSearchParams } from "react-router-dom"

/**
 * Deep-linkable 1-based page state stored in a URL query param (ADR-0006).
 *
 * Centralises the page-in-URL idiom for a paginated panel:
 *   - read the param, falling back to page 1 on a missing / invalid /
 *     non-positive value;
 *   - write it back with page 1 omitted, so the canonical link never
 *     carries a redundant `<key>=1`.
 *
 * The param `key` is supplied by the caller so several paginated panels
 * can share one route without their page state colliding. The sole
 * current consumer is the events panel (`ev_page`).
 *
 * `setPage` accepts a per-call `replace` so a caller can normalise the
 * URL silently (history-replacing, e.g. clamping a stale out-of-range
 * page) or push a new history entry (the default, e.g. a user click).
 */
export interface UrlPageParam {
  /** Current 1-based page (>= 1; 1 when the param is absent/invalid). */
  page: number
  /** Write the page to the URL (page 1 is omitted to keep links clean). */
  setPage: (next: number, opts?: { replace?: boolean }) => void
}

export const useUrlPageParam = (key: string): UrlPageParam => {
  const [searchParams, setSearchParams] = useSearchParams()

  const raw = Number.parseInt(searchParams.get(key) ?? "1", 10)
  const page = Number.isFinite(raw) && raw > 0 ? raw : 1

  const setPage = useCallback(
    (next: number, opts?: { replace?: boolean }) => {
      const url = new URLSearchParams(searchParams)
      // Page 1 is the canonical default — keep it out of the URL so the
      // clean link has no redundant `<key>=1`.
      if (next <= 1) url.delete(key)
      else url.set(key, String(next))
      setSearchParams(url, { replace: opts?.replace ?? false })
    },
    [searchParams, setSearchParams, key],
  )

  return { page, setPage }
}
